import _ from 'lodash';
import { BILLING_CYCLE } from '@/common/constants';
/**
 * Parse bundle prices from raw price data
 *
 * Bundle naming convention:
 * - Bundle total: JS_{jointsaleCode}_{billingCycle} (e.g., JS_pro_zsu_monthly)
 *   Must have exactly 3 underscores (JS_ + code + cycle)
 * - Bundle items: JS_{jointsaleCode}_{billingCycle}_{plancode} (e.g., JS_pro_zsu_monthly_zo_pro_monthly)
 *   Has 4 or more underscores
 * - Plan prices: Any key that doesn't match bundle patterns
 *
 * @param {Object} rawPrices - Raw price data from API
 * @returns {Object} Parsed data structure with allPrices, bundles, and planPrices
 */
export function parseBundlePrices(rawPrices) {
  // Bundle total price pattern: JS_pro_zsu_monthly, JS_biz_xxx_yearly, etc.
  // Matches: JS_{code}_{cycle} with exactly 3 underscores
  const bundlePattern = /^JS_(\w+)_(monthly|yearly)$/;

  // Bundle item pattern: JS_pro_zsu_monthly_zo_pro_monthly
  // Matches: JS_{code}_{cycle}_{plancode}
  const bundleItemPattern = /^(JS_\w+_(?:monthly|yearly))_(.+)$/;

  // Initialize result structure
  const result = {
    allPrices: {},
    bundles: {},
    planPrices: {}
  };

  // Step 1: Copy all prices to allPrices
  result.allPrices = { ...rawPrices };

  // Step 2: Identify all bundle totals
  // Filter out keys that have underscores after the billing cycle (those are bundle items)
  const bundleKeys = Object.keys(rawPrices).filter(key => {
    // Must match the bundle pattern
    if (!bundlePattern.test(key)) return false;

    // Count underscores - bundle total should have exactly 3 underscores (JS_ + code + cycle)
    const underscoreCount = (key.match(/_/g) || []).length;
    return underscoreCount === 3;
  });

  // Step 3: Process each bundle
  bundleKeys.forEach(bundleKey => {
    const match = bundleKey.match(bundlePattern);
    if (!match) return;

    const [, jointsaleCode, billingCycle] = match;
    const totalPrice = rawPrices[bundleKey];

    // Find all items belonging to this bundle
    const items = [];
    Object.keys(rawPrices).forEach(key => {
      const itemMatch = key.match(bundleItemPattern);
      if (itemMatch && itemMatch[1] === bundleKey) {
        const plancode = itemMatch[2]; // itemMatch[2] is the plancode part
        items.push({
          plancode,
          price: rawPrices[key]
        });
      }
    });

    // Create bundle object
    const bundleMapKey = `${jointsaleCode}_${billingCycle}`;
    result.bundles[bundleMapKey] = {
      bundleKey,
      jointsaleCode,
      billingCycle,
      totalPrice,
      items
    };
  });

  // Step 4: Identify plan prices
  // Plan prices are keys that don't start with JS_ (not bundle-related)
  Object.keys(rawPrices).forEach(key => {
    if (!key.startsWith('JS_')) {
      result.planPrices[key] = rawPrices[key];
    }
  });

  return result;
}

/**
 * Get bundle information by jointsale code and billing cycle
 *
 * @param {Object} bundles - Bundles map from parsed data
 * @param {string} jointsaleCode - Jointsale code (e.g., 'pro_zsu')
 * @param {string} billingCycle - Billing cycle ('monthly' or 'yearly')
 * @returns {Object|null} Bundle object or null if not found
 */
export function getBundleByKey(bundles, jointsaleCode, billingCycle) {
  // if billinCycle is annual, we need to convert it to yearly
  if (billingCycle === BILLING_CYCLE.ANNUAL) {
    billingCycle = 'yearly';
  }
  const key = `${jointsaleCode}_${billingCycle}`;
  return bundles[key] || null;
}

/**
 * Get price by plancode from allPrices map
 *
 * Note: This returns the standalone purchase price for a plancode.
 * For bundle prices, use getBundleByKey() instead.
 *
 * Example:
 * - getPriceByPlancode(allPrices, 'zo_pro_monthly') returns 1599 (standalone price)
 * - Bundle price for zo_pro_monthly in pro_zsu bundle is 1274 (discounted)
 *
 * @param {Object} allPrices - All prices map
 * @param {string} plancode - Plan code
 * @returns {number|string|null} Price or null if not found
 */
export function getPriceByPlancode(allPrices, plancode) {
  return allPrices[plancode] ?? null;
}

/**
 * Plan code delimiter for parsing comma-separated strings
 */
const PLAN_CODE_DELIMITER = ',';

/**
 * Parse plan code value to array
 * Handles both string (comma-separated) and array formats
 * Trims whitespace and filters out empty strings
 *
 * @param {string|Array} value - Plan code string (comma-separated) or array
 * @returns {Array} Parsed and cleaned plan code array
 */
function parsePlanCodeToArray(value) {
  if (!value) {
    return [];
  }

  if (_.isString(value)) {
    return _.chain(value)
      .split(PLAN_CODE_DELIMITER)
      .map(code => _.trim(code))
      .filter(code => code.length > 0)
      .value();
  }

  return _.isArray(value) ? value : [];
}

/**
 * Get bundle plan code list from partner config
 * Supports both new format (annualBundlePlans/monthlyBundlePlans) and legacy format (annualPreloadPlan/monthlyPreloadPlan)
 * New format can be either comma-separated string or array
 *
 * @param {Object} partnerConfig - Partner configuration object
 * @param {Object} partnerConfig.features - Features configuration
 * @param {string|Array} partnerConfig.features.annualBundlePlans - Annual bundle plans (new format, comma-separated string or array)
 * @param {string|Array} partnerConfig.features.monthlyBundlePlans - Monthly bundle plans (new format, comma-separated string or array)
 * @param {Array} partnerConfig.features.annualPreloadPlan - Annual preload plans (legacy format, array)
 * @param {Array} partnerConfig.features.monthlyPreloadPlan - Monthly preload plans (legacy format, array)
 * @returns {Array} Bundle plan code list
 */
export function getBundlePlanCodeListFromPartnerConfig(partnerConfig = {}) {
  const {
    annualBundlePlans: rawAnnualBundlePlans,
    monthlyBundlePlans: rawMonthlyBundlePlans,
    annualPreloadPlan = [],
    monthlyPreloadPlan = []
  } = partnerConfig?.features || {};

  // Parse new format, fallback to legacy format, default to empty array
  const parsedAnnualBundlePlans = parsePlanCodeToArray(rawAnnualBundlePlans);
  const parsedMonthlyBundlePlans = parsePlanCodeToArray(rawMonthlyBundlePlans);

  const annualPlans = parsedAnnualBundlePlans.length > 0
    ? parsedAnnualBundlePlans
    : (_.isArray(annualPreloadPlan) ? annualPreloadPlan : []);

  const monthlyPlans = parsedMonthlyBundlePlans.length > 0
    ? parsedMonthlyBundlePlans
    : (_.isArray(monthlyPreloadPlan) ? monthlyPreloadPlan : []);

  return [...annualPlans, ...monthlyPlans];
}
